import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { GenericCategory, GenericSection, GenericField } from "@shared/schema";

/**
 * INTERVIEW-SHELL-1 (s205) — resolve a section's ids ONCE, for every interview surface.
 *
 * 🔴 Extracted while fixing the s205 checkpoint defect, and the extraction IS part of the fix.
 * `CheckpointScreen` had hardcoded the string `"personal"` as a `categoryType`; the enum value is
 * `personal_info`, so the request 500'd and the card silently read an empty list. Resolving the type
 * from the category record — the way `CaptureUpstreamRenderer` already does — makes that class of bug
 * unrepresentable: there is no literal to get wrong.
 *
 * The second reason is G7. `PetsScreen` walked this same category → sections → fields chain, and the
 * pets screen is the template every remaining interview screen gets cloned from. Two copies before
 * SPEC lands would have become forty.
 *
 * Returns `undefined` ids while the chain is still resolving; callers gate their own queries on them.
 */
export function useSectionFields(
  clientId: string | undefined,
  categorySlug: string,
  sectionSlug: string,
) {
  const { data: category } = useQuery<GenericCategory>({
    queryKey: ["/api/generic-categories/by-slug", categorySlug, clientId],
    queryFn: async () =>
      (await apiRequest("GET", `/api/generic-categories/by-slug/${categorySlug}?clientId=${clientId}`)).json(),
    enabled: !!clientId,
  });

  const { data: sections = [] } = useQuery<GenericSection[]>({
    queryKey: ["/api/generic-categories", category?.id, "sections"],
    enabled: !!category?.id,
  });

  const sectionId = useMemo(
    () => sections.find((s) => s.slug === sectionSlug)?.id,
    [sections, sectionSlug],
  );

  const { data: fields = [] } = useQuery<GenericField[]>({
    queryKey: ["/api/generic-sections", sectionId, "fields"],
    enabled: !!sectionId,
  });

  const fieldIdByKey = useMemo(() => {
    const m = new Map<string, string>();
    fields.forEach((f) => m.set(f.fieldKey, f.id));
    return m;
  }, [fields]);

  return {
    categoryId: category?.id,
    /** Read from the category record — NEVER a hardcoded enum literal (see the header note). */
    categoryType: category?.categoryType,
    sectionId,
    fieldIdByKey,
    /** s222 LANE-2: the section's field records — a screen reads a field's own choices and `showIf`
     *  from here instead of re-typing them. */
    fields,
  };
}
